<template>
  <v-dialog :value="model.active" width="400" persistent>
    <v-card v-if="selected">
      <div class="d-flex justify-space-between align-center pr-2">
        <v-card-title class="text-h6 font-weight-bold py-2">
          <v-icon left>{{ selected.id ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
          Add to plan
        </v-card-title>
        <v-btn icon @click="closeDialog"><v-icon>fa-close</v-icon></v-btn>
      </div>
      <v-divider></v-divider>
      <v-card-subtitle class="font-weight-light mt-2 py-0">
        Choose one or more plans to assign this inquiry to. You can select multiple plans if needed.
      </v-card-subtitle>
      <v-card-text class="pt-5 pb-0">
        <v-form v-model="model.valid">
          <v-autocomplete
            v-model="selected.plans"
            :items="taskTypes"
            item-text="name"
            item-value="id"
            label="Select Plan"
            chips
            deletable-chips
            multiple
            small-chips
            outlined
            clearable
            color="primary"
            :rules="[formRules.required]"
            placeholder="Select Plan..."></v-autocomplete>
        </v-form>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-btn color="red" elevation="0" text @click="closeDialog"> close </v-btn>
        <v-spacer></v-spacer>
        <v-btn elevation="0" :disabled="!model.hasChanges" @click="discard"> discard </v-btn>
        <v-btn
          color="primary"
          :loading="updateLoading || model.saving"
          elevation="0"
          @click="saveChanges"
          :disabled="!model.valid || !model.hasChanges">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  const Autodesk = window.Autodesk;
  const THREE = window.THREE;

  import { $isEqual } from '@Utils';
  import formRules from '@Share/config/form-rules';
  import { mapGetters } from 'vuex';
  import { TaskFieldType, taskFieldList } from '@Share/config/enums';
  import { ForgeInspection, Linked360ImageInfo } from '@/scripts/js/forgeModels';
  import inspectionService from '../service/inspection.service';
  import { getDbIdForgeObject, getTagsFrom3DPoint } from '@/utils/forgeUtils';
  import ganttService from '@/features/Task/service/gantt.service';
  import { $formatDate } from '@/utils';
  import { log, warn, error } from '@/utils/debugLogger';
  export default {
    name: 'add-inspect-to-plan',
    data() {
      return {
        selected: {},
        selectedCemented: {},
        selectedInspects: [],
        TaskFieldType,
        formRules,
        taskFieldList,
        selectedType: null,
        model: {
          active: false,
          loading: false,
          valid: true,
          hasChanges: false,
          saving: false,
        },
      };
    },
    computed: {
      ...mapGetters('TASK', {
        updateLoading: 'GET_UPDATE_LOADING',
      }),
      images: {
        get() {
          return this.$store.getters['AI_INSPECT/IMAGES'];
        },
      },
      taskTypes: {
        get() {
          return this.$store.getters['TASK_MANAGER/GET_ENTRIES_BY_USER'];
        },
      },
      forgeService() {
        return this.$store.state.forgeService;
      },
      pageView3d() {
        return this.$store.getters['GET_PAGE_VIEW_3D'];
      },
      viewer() {
        return this.$store.state.viewer;
      },
      facility: {
        get() {
          return this.$store.getters['FACILITY/GET_FACILITY'];
        },
      },
    },
    methods: {
      open(item) {
        this.model.active = true;
        if (item) this.selectedInspects = structuredClone(item);
        this.cementSelected();
      },
      async saveChanges() {
        this.model.saving = true;

        if (this.selectedInspects.some((o) => !o.point3d)) {
          this.$dialog.notify.error('No 3D point found for one or more inspections');
          this.model.saving = false;
          return;
        }

        let inspections = await this.createdInspectionTags();

        let tasks = [];
        let promises = [];

        this.selected.plans.forEach((taskTypeId) => {
          let taskType = this.taskTypes.find((o) => o.id == taskTypeId);
          let statusId = null;
          if (taskType && taskType.statuses.length > 0) {
            statusId = taskType.statuses[0]?.id;
          }
          inspections.forEach(({ inspectionTag, inspectId }) => {
            let inspect = this.selectedInspects.find((o) => o.id == inspectId);
            let promise = ganttService.getNextId().then((e) => {
              let task = {
                text: `Inspect-${inspect.friendlyId}`,
                start_date: $formatDate(Date.now(), 'YYYY-MM-DD'),
                end_date: $formatDate(Date.now(), 'YYYY-MM-DD'),
                friendlyId: e.data.nextId,
                forgeTagsIds: [inspectionTag.id],
                progress: 0,
                typeId: taskTypeId,
                statusId: statusId,
              };
              tasks.push(task);
            });

            promises.push(promise);
          });
        });

        Promise.all(promises).then(() => {
          this.$store
            .dispatch('TASK/CREATE_MULTIPLE', tasks)
            .then(() => {
              this.$dialog.notify.success('Successfully added to plan');
              this.$store.dispatch('AI_INSPECT/GET')
              this.cementSelected();
              this.closeDialog();
            })
            .catch((error) => {
              error(error);
              this.$dialog.notify.error('Failed to add to plan');
            })
            .finally(() => {
              this.model.saving = false;
            });

            this.$store.dispatch('fetchAllAMISTagsDbids', {
            assetId: this.$route.params.id,
            fetched: false,
          });
        });
      },
      reset() {
        this.model.active = false;
        this.selected = {};
        this.selectedCemented = {};
      },
      closeDialog() {
        if (this.model.hasChanges) {
          const answer = window.confirm('You have unsaved changes on this page! leave anyway?');
          if (answer) {
            this.reset();
            this.discard();
          }
        } else {
          this.reset();
        }
      },
      discard() {
        this.selected = structuredClone(this.selectedCemented);
        this.model.hasChanges = false;
      },
      cementSelected() {
        this.selectedCemented = structuredClone(this.selected);
        this.checkForChanges();
      },
      checkForChanges() {
        if (!this.model.active) return;

        this.model.hasChanges = !$isEqual(this.selected, this.selectedCemented);
      },
      async initialize3dViewer() {
        if (!this.viewer) {
          this.$store.state.assetId = this.facility.id;
        }

        return this.$store
          .dispatch('START_VIEWER_3D')
          .then(() => {
            document.getElementById('forgeViewer')?.remove();

            let parentNode = document.getElementById('viewer3d');

            if (!parentNode)
              parentNode = document.getElementById('viewer3d-temp');

            if (!parentNode) return;

            if (parentNode?.firstChild) {
              parentNode.insertBefore(this.pageView3d, parentNode.firstChild);
            } else {
              parentNode.appendChild(this.pageView3d);
            }

            return this.setupViewerCallback();
          })
          .catch((error) => {
            error('Error starting viewer', error);
          });
      },
      setupViewerCallback() {
        return new Promise((resolve, reject) => {
          try {
            let callback = () => {
              setTimeout(() => {
                resolve('Viewer is ready');
                this.viewer.removeEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, callback);
              }, 100);
            };

            if (this.viewer?.model?.isLoadDone()) {
              callback();
            } else {
              this.viewer.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, callback);
            }
          } catch (error) {
            reject(error);
          }
        });
      },
      async createdInspectionTags() {
        await this.initialize3dViewer();

        const promises = await Promise.all(
          this.selectedInspects.map(async (inspect) => {
            let image = this.images.find((o) => o.id == inspect.imageId);

            this.viewer?.isolate();

            if (!image) {
              error('Image not found', inspect.imageId);
              return null;
            }

            let imagePt = image.getCameraPositionInViewer();

            const tags = getTagsFrom3DPoint(this.viewer, inspect.point3d, imagePt);

            let dbIds = [];
            let children = [];

            if (tags.length) {
              dbIds = tags.flatMap((p) => p.children.map((c) => c.dbId));
              let promises = [];
              dbIds.forEach((dbId) => {
                promises.push(getDbIdForgeObject(dbId, this.viewer));
              });

              await Promise.all(promises).then((res) => {
                children = res.flatMap((p) => p);
              });
            }

            let point = new THREE.Vector3(inspect.point3d.x, inspect.point3d.y, inspect.point3d.z);

            var inspectionTag = new ForgeInspection();
            inspectionTag.name = 'Inspect-' + inspect.friendlyId;
            inspectionTag.anomalyInsightId = inspect.id;
            inspectionTag.severity = inspect.severity;
            inspectionTag.pltName = "N/A"
            inspectionTag.createDate = new Date().toDateString();
            inspectionTag.point = this.forgeService.view3dToWorld(point);
            inspectionTag.children = children;
            inspectionTag.linked360ImageInfo = new Linked360ImageInfo();
            inspectionTag.linked360ImageInfo.imageId = inspect.imageId;
            inspectionTag.thumbnailUrl = inspect.tiledImageUriPath;

            const { data } = await inspectionService.add(inspectionTag, dbIds, this.facility.id);
            
            return {
              inspectionTag: data,
              inspectId: inspect.id,
            };
          }),
        );

        return promises.filter((result) => result !== null);
      },
    },
    watch: {
      selected: {
        handler() {
          this.checkForChanges();
        },
        deep: true,
      },
    },
  };
</script>
